import React, { useMemo, useState } from 'react';
import axiosInstance from '../utils/axiosInstance';
import { useConfirm } from '../context/ConfirmContext';
import { extractErrorMessage } from '../utils/extractErrorMessage';
import Button from './ui/Button';
import ActionRow from './ui/ActionRow';
import { Checkbox } from './pilot-ui/checkbox';
import { Input } from './pilot-ui/input';
import { Label } from './pilot-ui/label';
import { Textarea } from './pilot-ui/textarea';
import {
  derivedRowErrorMessage, derivedRowNote, isDerivedSource,
} from '../utils/derivedCatalogRow';
import FilterIdentityCurveFields, {
  IdentityCurveDraft,
} from './FilterIdentityCurveFields';

/**
 * FS-504: the unified catalog row editor. Replaces the old inline edit form
 * whose PUT silently dropped curve fields. One form, two write paths:
 *
 *  - Scalar metadata (and a model rename alone) go through PUT with ONLY the
 *    changed fields.
 *  - Identity or curve changes publish a manual-source revision through the
 *    proposal pipeline (from-manual → accept), which owns curve validation,
 *    derived-field regeneration, override upkeep and provenance. When the
 *    family (brand/size/depth/MERV) is unchanged the accept targets this row
 *    via update_existing; a move to an occupied family stays PENDING in the
 *    Label Proposals panel (and no metadata is written); a move to an empty
 *    family creates a new row and offers to delete the original.
 *
 * Combined saves: proposal/accept FIRST, then the scalar diff PUTs onto the
 * row the accept returned — never blindly onto the original.
 */

export interface CatalogRow {
  id: number;
  brand: string;
  model: string;
  manufacturer: string | null;
  depth_inches: number;
  nominal_size: string;
  merv: number | null;
  merv_equivalent: number;
  maedb_model_number: string | null;
  mpr: number | null;
  fpr: number | null;
  performance_tier: string | null;
  dp_at_300fpm: number | null;
  filter_life_months: number | null;
  max_airflow_cfm: number | null;
  c_coefficient: number | null;
  title20_listed: boolean;
  notes: string | null;
  dp_curve: [number, number][] | null;
  source: string | null;
  source_meta?: Record<string, unknown> | null;
}

/** PUT-whitelisted scalar fields (server drops everything else). */
interface ScalarDraft {
  mpr: string;
  fpr: string;
  merv_equivalent: string;
  performance_tier: string;
  dp_at_300fpm: string;
  filter_life_months: string;
  max_airflow_cfm: string;
  title20_listed: boolean;
  notes: string;
}

const toDraft = (row: CatalogRow): IdentityCurveDraft => ({
  brand: row.brand ?? '',
  manufacturer: row.manufacturer ?? '',
  model: row.model ?? '',
  nominal_size: row.nominal_size ?? '',
  depth_inches: row.depth_inches != null ? String(row.depth_inches) : '1',
  merv: row.merv != null ? String(row.merv) : String(row.merv_equivalent ?? ''),
  maedb_model_number: row.maedb_model_number ?? '',
  curve: (row.dp_curve ?? []).map(([r, c]) => ({
    resistance_iwc: String(r), cfm: String(c),
  })),
});

const toScalars = (row: CatalogRow): ScalarDraft => ({
  mpr: row.mpr != null ? String(row.mpr) : '',
  fpr: row.fpr != null ? String(row.fpr) : '',
  merv_equivalent: row.merv_equivalent != null ? String(row.merv_equivalent) : '',
  performance_tier: row.performance_tier ?? '',
  dp_at_300fpm: row.dp_at_300fpm != null ? String(row.dp_at_300fpm) : '',
  filter_life_months: row.filter_life_months != null ? String(row.filter_life_months) : '',
  max_airflow_cfm: row.max_airflow_cfm != null ? String(row.max_airflow_cfm) : '',
  // c_coefficient is curve-derived — regenerated at accept, never hand-edited (PUT rejects it).
  title20_listed: row.title20_listed,
  notes: row.notes ?? '',
});

const numOrNull = (s: string): number | null => (s.trim() === '' ? null : Number(s));
// JSON persists both signed zeros as 0; a missing value remains distinct.
const samePersistedNumber = (left: number | null, right: number | null): boolean => left === right;

const FilterRowEditor: React.FC<{
  baseUrl: string;
  row: CatalogRow;
  onClose: () => void;
  onSaved: () => void;
}> = ({ baseUrl, row, onClose, onSaved }) => {
  const confirm = useConfirm();
  const [draft, setDraft] = useState<IdentityCurveDraft>(() => toDraft(row));
  const [scalars, setScalars] = useState<ScalarDraft>(() => toScalars(row));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  // One idempotency key per revision draft, kept across failed retries so
  // the server never creates duplicate proposals (Plan 109 discipline).
  const [idemKey, setIdemKey] = useState(() => crypto.randomUUID());

  const original = useMemo(() => toDraft(row), [row]);
  const originalScalars = useMemo(() => toScalars(row), [row]);

  const identityDirty =
    draft.brand.trim() !== original.brand.trim() ||
    draft.nominal_size.trim() !== original.nominal_size.trim() ||
    draft.depth_inches !== original.depth_inches ||
    !samePersistedNumber(numOrNull(draft.merv), numOrNull(original.merv)) ||
    draft.maedb_model_number.trim() !== original.maedb_model_number.trim();

  const curveDirty = JSON.stringify(
    draft.curve.map(p => [numOrNull(p.resistance_iwc), numOrNull(p.cfm)]),
  ) !== JSON.stringify(
    original.curve.map(p => [numOrNull(p.resistance_iwc), numOrNull(p.cfm)]),
  );

  const modelDirty = draft.model.trim() !== original.model.trim();

  // Auto-target this row via update_existing ONLY when the COMPLETE identity
  // is unchanged — including the MAEDbS number. A changed number means a
  // different product: the accept must go through the conflict state machine
  // (pending-resolution in the panel), not silently overwrite this row — and
  // update_existing with a differing number is a server 400, which would
  // strand the proposal behind a generic error (review finding 2026-08-20).
  // Brand compared verbatim: a casing change alters the display brand and is
  // treated as a family move so the server decides.
  const familyUnchanged =
    draft.brand.trim() === original.brand.trim() &&
    draft.nominal_size.trim() === original.nominal_size.trim() &&
    draft.depth_inches === original.depth_inches &&
    samePersistedNumber(numOrNull(draft.merv), numOrNull(original.merv)) &&
    draft.maedb_model_number.trim() === original.maedb_model_number.trim();

  const proposalNeeded = identityDirty || curveDirty;

  const scalarDiff = (): Record<string, unknown> => {
    const diff: Record<string, unknown> = {};
    const numericFields = [
      'mpr', 'fpr', 'merv_equivalent', 'dp_at_300fpm',
      'filter_life_months', 'max_airflow_cfm',
    ] as const;
    for (const field of numericFields) {
      const next = numOrNull(scalars[field]);
      if (!samePersistedNumber(next, numOrNull(originalScalars[field]))) diff[field] = next;
    }
    const tier = scalars.performance_tier.trim() || null;
    if (tier !== (originalScalars.performance_tier.trim() || null)) diff.performance_tier = tier;
    if (scalars.title20_listed !== originalScalars.title20_listed) {
      diff.title20_listed = scalars.title20_listed;
    }
    const notes = scalars.notes.trim() || null;
    if (notes !== (originalScalars.notes.trim() || null)) diff.notes = notes;
    // Model + manufacturer ride the accept when a proposal is published;
    // otherwise they are plain PUT fields. Exception: CLEARING manufacturer
    // cannot ride the accept — the server reads null as "preserve the target
    // row's manufacturer" — so an explicit clear goes in the follow-up PUT
    // (review finding 2026-08-20).
    if (!proposalNeeded) {
      if (modelDirty) diff.model = draft.model.trim();
      if (draft.manufacturer.trim() !== original.manufacturer.trim()) {
        diff.manufacturer = draft.manufacturer.trim() || null;
      }
    } else if (original.manufacturer.trim() !== '' && draft.manufacturer.trim() === '') {
      diff.manufacturer = null;
    }
    return diff;
  };

  const setScalar = (field: keyof ScalarDraft, value: string | boolean) =>
    setScalars(s => ({ ...s, [field]: value }));

  // FS-686: scaled and extrapolated rows are regenerated nightly from a base
  // row; the API refuses to change them (409 derived_row), so Save is off.
  const derived = isDerivedSource(row.source);

  const save = async () => {
    setError('');
    setNotice('');
    if (derived) {
      setError(`${derivedRowNote(row.source_meta)}.`);
      return;
    }
    if (proposalNeeded && draft.curve.length < 2) {
      setError('Identity and curve changes publish a revision, which needs a '
        + 'pressure curve with at least 2 points. Add curve points, or cancel '
        + 'and edit only name/metadata.');
      return;
    }
    setSaving(true);
    let targetId = row.id;
    try {
      if (proposalNeeded) {
        const prop = await axiosInstance.post(`${baseUrl}/label-proposals/from-manual`, {
          idempotency_key: idemKey,
          candidate_brand: draft.brand.trim(),
          candidate_model: draft.model.trim() || null,
          candidate_nominal_size: draft.nominal_size.trim(),
          candidate_depth_inches: Number(draft.depth_inches),
          candidate_merv: Number(draft.merv),
          candidate_maedb_model_number: draft.maedb_model_number.trim() || null,
          candidate_curve: draft.curve.map(pt => ({
            resistance_iwc: Number(pt.resistance_iwc),
            cfm: Number(pt.cfm),
          })),
          note: `edited from catalog row #${row.id}`,
        });
        try {
          const acc = await axiosInstance.post(
            `${baseUrl}/label-proposals/${prop.data.id}/accept`, {
              brand: draft.brand.trim(),
              manufacturer: draft.manufacturer.trim() || null,
              model: draft.model.trim() || null,
              nominal_size: draft.nominal_size.trim(),
              depth_inches: Number(draft.depth_inches),
              merv: Number(draft.merv),
              maedb_model_number: draft.maedb_model_number.trim() || null,
              curve: draft.curve.map(pt => ({
                resistance_iwc: Number(pt.resistance_iwc),
                cfm: Number(pt.cfm),
              })),
              note: null,
              ...(familyUnchanged
                ? { conflict_resolution: { action: 'update_existing', catalog_id: row.id } }
                : {}),
            });
          targetId = acc.data?.catalog_row?.id ?? row.id;
        } catch (err: unknown) {
          const data = (err as { response?: { data?: { error?: string } } })
            ?.response?.data;
          if (data?.error === 'catalog_conflict') {
            // Occupied target family: the proposal stays pending; nothing
            // else is written (metadata included) and the draft is kept.
            setNotice(`The new identity conflicts with existing catalog entries. `
              + `Proposal #${prop.data.id} is pending — resolve it in the Label `
              + `Proposals panel above. Your metadata changes were NOT applied.`);
            setSaving(false);
            return;
          }
          const derivedMsg = derivedRowErrorMessage(err, row.source_meta);
          if (derivedMsg) {
            throw new Error(`${derivedMsg} Proposal #${prop.data.id} is still `
              + `pending in the Label Proposals panel — reject it there.`);
          }
          // Any other accept failure also leaves the proposal pending — say
          // so instead of a bare generic error (review finding 2026-08-20).
          throw new Error(`${extractErrorMessage(err, 'Publishing the revision failed')} `
            + `(proposal #${prop.data.id} was created and is still pending in the `
            + `Label Proposals panel — resolve or reject it there).`);
        }
        // The revision published — a retry from here on is a new draft.
        setIdemKey(crypto.randomUUID());
      }

      const diff = scalarDiff();
      if (Object.keys(diff).length > 0) {
        await axiosInstance.put(`${baseUrl}/${targetId}`, diff);
      }

      if (proposalNeeded && !familyUnchanged && targetId !== row.id) {
        const del = await confirm({
          title: 'Delete the original row?',
          message: `The revised identity published as a new catalog entry `
            + `(#${targetId}). The original entry #${row.id} still holds the `
            + `old identity. Delete it? Devices pointing at it re-match on `
            + `the next nightly run.`,
          confirmLabel: 'Delete original',
          tone: 'danger',
        });
        if (del) await axiosInstance.delete(`${baseUrl}/${row.id}`);
      }

      onSaved();
      onClose();
    } catch (err: unknown) {
      setError(derivedRowErrorMessage(err, row.source_meta)
        ?? extractErrorMessage(err, 'Failed to save'));
    } finally {
      setSaving(false);
    }
  };

  const certifiedDowngrade = proposalNeeded &&
    (row.source === 'maedb' || row.source === 'filter_label');
  const hasChanges = proposalNeeded || Object.keys(scalarDiff()).length > 0;

  const scalarInput = (
    field: Exclude<keyof ScalarDraft, 'title20_listed' | 'notes'>,
    label: string,
    help: string,
    type: 'text' | 'number' = 'number',
    step?: string,
  ) => {
    const id = `filter-edit-${row.id}-${field}`;
    return (
      <div>
        <Label htmlFor={id} className="block text-xs mb-0.5">{label}</Label>
        <Input id={id} type={type} step={step} value={scalars[field]}
          aria-label={`Edit ${label}`} aria-describedby={`${id}-help`}
          onChange={e => setScalar(field, e.target.value)} />
        <p id={`${id}-help`} className="text-xs text-muted-foreground">{help}</p>
      </div>
    );
  };

  return (
    <div className="bg-card border border-border rounded-lg shadow-md p-4 mt-4">
      <h3 className="text-lg font-bold mb-1">
        Edit Filter <span className="text-gray-600 font-normal">#{row.id}</span>
      </h3>
      {error && (
        <div className="border border-status-error-border bg-status-error text-status-error-foreground p-2 rounded-md my-2 text-sm" role="alert">{error}</div>
      )}
      {notice && (
        <div className="bg-amber-50 text-amber-800 border border-status-warning-border p-2 rounded-md my-2 text-sm">
          {notice}
        </div>
      )}
      {derived && (
        <div data-testid="derived-row-note"
          className="bg-gray-50 text-gray-700 border border-border p-2 rounded-md my-2 text-sm">
          {derivedRowNote(row.source_meta)}.
        </div>
      )}

      <h4 className="text-sm font-bold mt-2 mb-2">Name &amp; metadata</h4>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
        {scalarInput('mpr', 'MPR', 'Microparticle performance rating, if published.')}
        {scalarInput('fpr', 'FPR', 'Filter performance rating, if published.')}
        {scalarInput('merv_equivalent', 'MERV Equivalent', 'Equivalent MERV rating shown in the catalog.')}
        {scalarInput('performance_tier', 'Performance Tier', 'Published performance tier for this filter.', 'text')}
        {scalarInput('dp_at_300fpm', 'ΔP @300fpm', 'Pressure drop at 300 feet per minute.', 'number', '0.001')}
        {scalarInput('filter_life_months', 'Filter Life (mo)', 'Expected filter life in months.')}
        {scalarInput('max_airflow_cfm', 'Max Airflow CFM', 'Maximum rated airflow in cubic feet per minute.')}
        <div>
          <div className="flex items-center gap-2">
            <Checkbox id={`filter-edit-${row.id}-title20`} checked={scalars.title20_listed}
              onCheckedChange={next => setScalar('title20_listed', next)}
              aria-describedby={`filter-edit-${row.id}-title20-help`} />
            <Label htmlFor={`filter-edit-${row.id}-title20`}>Title 20 Listed</Label>
          </div>
          <p id={`filter-edit-${row.id}-title20-help`} className="text-xs text-muted-foreground">Whether this filter is listed under Title 20.</p>
        </div>
        <div className="col-span-2">
          <Label htmlFor={`filter-edit-${row.id}-notes`} className="block text-xs mb-0.5">Notes</Label>
          <Textarea id={`filter-edit-${row.id}-notes`} value={scalars.notes} aria-label="Edit Notes"
            aria-describedby={`filter-edit-${row.id}-notes-help`}
            onChange={e => setScalar('notes', e.target.value)} />
          <p id={`filter-edit-${row.id}-notes-help`} className="text-xs text-muted-foreground">Catalog notes about this filter.</p>
        </div>
      </div>

      <h4 className="text-sm font-bold mt-4 mb-2">Identity &amp; pressure curve</h4>
      <p className="text-xs text-amber-700 bg-amber-50 border border-status-warning-border rounded-sm p-2 mb-2">
        Saving identity or curve changes publishes a manual-source revision —
        certified rows are downgraded and never used for CFM calibration.
        {certifiedDowngrade && (
          <span className="font-semibold"> This row is certified today.</span>
        )}
      </p>
      <FilterIdentityCurveFields
        value={draft}
        onChange={setDraft}
        showManufacturer
        ariaPrefix="Edit "
      />

      <ActionRow
        className="mt-4"
        reason={(derived || !hasChanges) && (derived ? derivedRowNote(row.source_meta) : 'No changes to save.')}
        reasonId={`filter-edit-${row.id}-save-reason`}
      >
        <Button variant="primary" onClick={save} disabled={saving || derived || !hasChanges}
          aria-describedby={!hasChanges || derived ? `filter-edit-${row.id}-save-reason` : undefined}
          title={derived ? derivedRowNote(row.source_meta) : 'Save the changes to this catalog entry'}>
          {saving ? 'Saving...' : 'Save'}
        </Button>
        <Button variant="secondary" onClick={onClose}
          title="Close the editor without saving. The catalog entry stays as it was.">
          Cancel
        </Button>

      </ActionRow>
    </div>
  );
};

export default FilterRowEditor;
